import { createSelector } from 'reselect';
import {
  Company,
  DataCutDisplayMode,
  RoleCode,
  TypeOfPay,
  User,
  UserSettings,
} from 'types';
import { defaultSettings } from './constants';
import { RootState } from '../reducer';

export function selectUser(state: RootState) {
  return state.user.userData as User;
}

export function selectUserCompany(state: RootState) {
  const user = selectUser(state);
  return user?.Company as Company;
}

export function selectIsUserInOriginCompany(state: RootState) {
  const user = selectUser(state);
  return user?.companyId === user?.originCompanyId;
}

export function selectIsSuperUser(state: RootState) {
  const user = selectUser(state);
  return user?.roles?.find(role => role.code === RoleCode.SUPER_ADMIN);
}

export function selectIsMultiTenant(state: RootState) {
  const user = selectUser(state);
  // Check if user has multi-tenancy access based on JWT claims
  // This would be set when the JWT contains mt: true
  return (user as any)?.mt === true;
}

export function selectUserSettings(state: RootState) {
  const user = selectUser(state);
  return (user && user.settings) ? user.settings : {};
}

export function selectMassPriceJobActiveDataPointConfig(state: RootState) {
  const settings = selectUserSettings(state);
  const activeDataPoint = settings.massPriceJobActiveDataPoint;

  if (Object.values(TypeOfPay).includes(activeDataPoint)) {
    return activeDataPoint;
  }

  return TypeOfPay.BASE;
}

export function selectDataCutDisplayModeConfig(state: RootState) {
  const settings = selectUserSettings(state);
  const mode = settings.dataCutDisplayMode;

  if (Object.values(DataCutDisplayMode).includes(mode)) {
    return mode;
  }

  return DataCutDisplayMode.RAW;
}

export function selectUserPriceJobTableConfig(state: RootState) {
  const settings = selectUserSettings(state);
  return settings.priceJobTableConfig || defaultSettings.priceJobTableConfig;
}

export function selectUserMassPriceJobTableConfig(state: RootState) {
  const settings = selectUserSettings(state);
  return settings.massPriceJobTableConfig || defaultSettings.massPriceJobTableConfig;
}

export function selectUserMassPriceJobRecordsPerPage(state: RootState) {
  const settings = selectUserSettings(state);
  return settings.massPriceJobRecordsPerPage || defaultSettings.massPriceJobRecordsPerPage;
}

export function selectUserAddSurveyDataTableConfig(state: RootState) {
  const settings = selectUserSettings(state);
  return settings.addSurveyDataTableConfig || defaultSettings.addSurveyDataTableConfig;
}

export function selectUserModelCompositesTableConfig(state: RootState) {
  const settings = selectUserSettings(state);
  return settings.modelCompositesTableConfig || defaultSettings.modelCompositesTableConfig;
}

export function selectUserModelSalaryRangeDetailsTableConfig(state: RootState) {
  const settings = selectUserSettings(state);
  return settings.modelSalaryRangeDetailsTableConfig || defaultSettings.modelSalaryRangeDetailsTableConfig;
}

export function selectUserMassPriceSurveyConfig(state: RootState) {
  const settings = selectUserSettings(state);
  return settings.massPriceSurveyConfig || {};
}

export function selectJobMappingTableConfig(state: RootState) {
  const settings = selectUserSettings(state);
  return settings.companyJobMapping || defaultSettings.companyJobMapping;
}

export function selectJobMappingRecommendationsTableConfig(state: RootState) {
  const settings = selectUserSettings(state);
  return settings.companyJobMappingRecommendations || defaultSettings.companyJobMappingRecommendations;
}

export function selectLevelMappingTableConfig(state: RootState) {
  const settings = selectUserSettings(state);
  return settings.companyLevelMapping || defaultSettings.companyLevelMapping;
}

export function selectUserMassJobMappingConfig(state: RootState) {
  const settings = selectUserSettings(state);
  return settings.massJobMappingConfig || [];
}

export function selectJobTableConfig(state: RootState) {
  const settings = selectUserSettings(state);
  return settings.companyJobTableConfig || defaultSettings.companyJobTableConfig;
}

export function selectLevelTableConfig(state: RootState) {
  const settings = selectUserSettings(state);
  return settings.companyLevelConfig || defaultSettings.companyLevelConfig;
}

export function selectCompositePercentilesTableConfig(state: RootState) {
  const settings = selectUserSettings(state);
  return settings.compositePercentilesTableConfig || defaultSettings.compositePercentilesTableConfig;
}

export function selectEmployeesTableConfig(state: RootState) {
  const settings = selectUserSettings(state);
  return settings.employeesTableConfig || defaultSettings.employeesTableConfig;
}

export const selectUserSettingsByKey = (settingKey: keyof UserSettings) => createSelector(
  selectUserSettings,
  (settings) => settings[settingKey] || defaultSettings[settingKey] || [],
);

export function selectModelRangeSetTableConfig(state: RootState) {
  const settings = selectUserSettings(state);
  return settings.modelRangeSetTableConfig || defaultSettings.modelRangeSetTableConfig;
}

export function selectUserRangAssignmentTableConfig(state: RootState) {
  const settings = selectUserSettings(state);
  return settings.rangeAssignmentsTableConfig || defaultSettings.rangeAssignmentsTableConfig;
}


export function selectUserActiveVendorLevelProfileId (state: RootState) {
  const settings = selectUserSettings(state);
  return settings.activeVendorLevelProfile || null;
}
